/**
 * Brand kits API surface — typed wrappers around /v1/brand-kits per
 * ED-A5 / Sprint 3 T3.2.
 *
 * The `data` jsonb is loosely-typed at the wire level (the editor + the
 * BrandKit form panel are the source of truth for the schema). Strong
 * typing happens in the consumers via the BrandKitData interface below.
 */
import { apiClient } from "./apiClient";

export interface BrandKitColors {
  primary?: string;
  primary_contrast?: string;
  background?: string;
  surface?: string;
  text_primary?: string;
  text_secondary?: string;
  border?: string;
  accent?: string;
  link?: string;
  success?: string;
  warning?: string;
  error?: string;
  dark?: Partial<BrandKitColors>;
  [k: string]: unknown;
}

export interface BrandKitFontDefinition {
  family?: string;
  weight?: number;
  line_height?: number;
  letter_spacing?: string;
}

export interface BrandKitTypography {
  body_font?: BrandKitFontDefinition;
  heading_font?: BrandKitFontDefinition;
  monospace_font?: BrandKitFontDefinition;
  font_size_base?: number;
  custom_fonts?: Array<{ family: string; url: string; weights?: number[] }>;
}

export interface BrandKitLogo {
  url?: string;
  url_dark?: string;
  width?: number;
  alt?: string;
}

export interface BrandKitSocial {
  platform: string;
  url: string;
  handle?: string;
}

export interface BrandKitFooter {
  company_name?: string;
  address?: string;
  unsubscribe_text?: string;
  legal_text?: string;
}

/** AI-09 §B1 — enums mirrored in Laravel validateVoice + ai-service VoiceService. */
export const VOICE_TONES = [
  "formal",
  "casual",
  "playful",
  "professional",
  "friendly",
  "authoritative",
] as const;
export type VoiceTone = (typeof VOICE_TONES)[number];

export const VOICE_LANGUAGE_LEVELS = [
  "conversational",
  "technical",
  "casual",
  "formal",
] as const;
export type VoiceLanguageLevel = (typeof VOICE_LANGUAGE_LEVELS)[number];

export const VOICE_EXAMPLE_CATEGORIES = [
  "subject_line",
  "cta",
  "hero",
  "body",
] as const;
export type VoiceExampleCategory = (typeof VOICE_EXAMPLE_CATEGORIES)[number];

export interface BrandVoiceExample {
  category: VoiceExampleCategory;
  text: string;
}

/**
 * Brand voice profile stored in brand_kits.data.voice (AI-09 P1
 * decision 1). Quotas enforced server-side: banned ≤50, phrases ≤20,
 * examples ≤10 each.
 */
export interface BrandKitVoice {
  tone?: VoiceTone | null;
  custom_tone?: string | null;
  personality_traits?: string[];
  language_level?: VoiceLanguageLevel | null;
  banned_words?: string[];
  signature_phrases?: string[];
  tone_description?: string | null;
  color_usage?: Record<string, string[]>;
  typography_guidance?: string | null;
  examples?: { good?: BrandVoiceExample[]; avoid?: BrandVoiceExample[] };
  source?: "manual" | "trained";
  trained_at?: string | null;
  updated_at?: string;
}

export interface BrandKitData {
  colors?: BrandKitColors;
  typography?: BrandKitTypography;
  logo?: BrandKitLogo;
  favicon_url?: string;
  social?: BrandKitSocial[];
  footer?: BrandKitFooter;
  sender?: { name?: string; email?: string; reply_to?: string };
  voice?: BrandKitVoice;
  [k: string]: unknown;
}

export interface BrandKit {
  id: string;
  tenant_id: string;
  name: string;
  is_default: boolean;
  data: BrandKitData;
  created_at: string;
  updated_at: string;
}

interface Wrapped<T> {
  data: T;
}

export async function listBrandKits(): Promise<BrandKit[]> {
  const res = await apiClient.get<{ data: BrandKit[] }>("/v1/brand-kits");
  return res.data;
}

export async function getDefaultBrandKit(): Promise<BrandKit> {
  const res = await apiClient.get<Wrapped<BrandKit>>("/v1/brand-kits/default");
  return res.data;
}

export async function getBrandKit(id: string): Promise<BrandKit> {
  const res = await apiClient.get<Wrapped<BrandKit>>(`/v1/brand-kits/${id}`);
  return res.data;
}

export interface CreateBrandKitInput {
  name: string;
  is_default?: boolean;
  data?: BrandKitData;
}

export async function createBrandKit(
  input: CreateBrandKitInput,
): Promise<BrandKit> {
  const res = await apiClient.post<Wrapped<BrandKit>>("/v1/brand-kits", input);
  return res.data;
}

/**
 * RFC 7396 merge-patch+json. The server merges `data` recursively and
 * replaces top-level model fields shallowly. `null` values inside `data`
 * delete the field.
 */
export interface BrandKitPatch {
  name?: string;
  is_default?: boolean;
  data?: Record<string, unknown>;
}

export async function patchBrandKit(
  id: string,
  patch: BrandKitPatch,
): Promise<BrandKit> {
  const res = await apiClient.patch<Wrapped<BrandKit>>(
    `/v1/brand-kits/${id}`,
    patch,
  );
  return res.data;
}

export async function deleteBrandKit(id: string): Promise<void> {
  await apiClient.delete(`/v1/brand-kits/${id}`);
}
